import { useState } from "react";
import useDataset from "datasets/hooks/useDataset";
import Dataset from "datasets/types/interfaces/dataset";
import { resolveDatasetAccentColor } from "datasets/utils/color";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { ACCENT_PALETTE, resolveAccentPaletteColor } from "~/lib/accentPalette";
import { SettingsSection } from "~/pages/manifold-nextgen/components/settings/SettingsSection";
import { apiMutate } from "~/utils/apiClient";

const { VITE_CATALOG_API } = import.meta.env;

interface DatasetAppearanceSectionProps {
  dataset: Dataset;
}

export const DatasetAppearanceSection = ({ dataset }: DatasetAppearanceSectionProps) => {
  const { mutate } = useDataset();
  const { showSuccess, showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // The last-saved color, used both as the Reset target and to decide whether the
  // form is dirty. Updated on a successful save so Save/Reset re-disable immediately,
  // without waiting on the parent to re-fetch and pass down a fresh `dataset` prop.
  const [committedColor, setCommittedColor] = useState(
    () => resolveDatasetAccentColor(dataset.accentColor).accent
  );
  const [color, setColor] = useState(committedColor);

  const isDirty = color !== committedColor;
  const previewColor = resolveAccentPaletteColor(color);

  const handleReset = () => setColor(committedColor);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await apiMutate(`${VITE_CATALOG_API}/datasets/${dataset.id}`, "PATCH", {
        accentColor: color,
      });
      setCommittedColor(color);
      await mutate();
      showSuccess("Appearance saved");
    } catch {
      showError("Failed to save appearance");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingsSection title="Appearance">
      <div className="grid grid-cols-[280px_1fr] gap-8 px-4 py-4">
        <div className="flex flex-col items-center gap-3">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Preview
          </span>
          <div className="h-[150px] w-[200px] overflow-hidden rounded-lg border border-border">
            <div className="h-9" style={{ backgroundColor: previewColor.bg }} />
            <div className="flex flex-col gap-1.5 bg-card px-3 py-3">
              <p className="truncate text-sm font-semibold text-card-foreground">{dataset.title}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-5">
          <div>
            <p className="mb-2 text-sm font-medium text-foreground">Color</p>
            <div className="flex flex-wrap gap-2.5">
              {ACCENT_PALETTE.map((swatch) => (
                <button
                  key={swatch.accent}
                  type="button"
                  aria-label={`Use color ${swatch.name}`}
                  aria-pressed={color === swatch.accent}
                  onClick={() => setColor(swatch.accent)}
                  className="size-7 cursor-pointer rounded-full border-2"
                  style={{
                    backgroundColor: swatch.bg,
                    borderColor: swatch.accent,
                    boxShadow:
                      color === swatch.accent
                        ? `0 0 0 2px var(--background), 0 0 0 4px ${swatch.accent}`
                        : undefined,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 px-4 py-3">
        <Button variant="ghost" onClick={handleReset} disabled={!isDirty || isSaving}>
          Reset
        </Button>
        <Button onClick={handleSave} disabled={!isDirty || isSaving} loading={isSaving}>
          Save
        </Button>
      </div>
    </SettingsSection>
  );
};
